import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./lib/auth";
import { Toaster } from "./components/ui/sonner";
import GlobalLoadingOverlay from "./components/global-loading-overlay";
import { ThemeProvider } from "./components/theme-provider";

import Index from "./routes/01_index/index";
const Dashboard = React.lazy(() => import("./routes/02_dashboard/dashboard"));
const MyProfile = React.lazy(() => import("./routes/03_profile/my-profile"));
const Personnel = React.lazy(() => import("./routes/04_personnel/personnel"));
const Office = React.lazy(() => import("./routes/06_office/office"));
const Units = React.lazy(() => import("./routes/05_station/station"));
const DataReference = React.lazy(() => import("./routes/07_datareference/data-reference"));
const Settings = React.lazy(() => import("./routes/10_settings/settings"));
const Location = React.lazy(() => import("./routes/08_location/location"));
const SystemAccess = React.lazy(() => import("./routes/09_systemaccess/system-access"));
const AvailableUsers = React.lazy(() => import("./routes/12_users/AvailableUsers"));
const ActiveUsers = React.lazy(() => import("./routes/12_users/ActiveUsers"));

import AuthGate from "./components/auth-gate";
import RouteSuspenseFallback from "./components/route-suspense-fallback";
import ErrorBoundary from "./components/error-boundary";

const queryClient = new QueryClient();

function NotFound() {
  const { user } = useAuth();
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-background to-muted px-4">
      <div className="mx-auto w-full max-w-4xl rounded-lg bg-card p-8 shadow-lg">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="flex flex-col justify-center">
            <div className="mb-4">
              <div className="text-sm font-semibold text-muted-foreground">CDMS</div>
              <div className="text-xs text-muted-foreground/70">
                Bureau of Fire Protection — MIMAROPA Region
              </div>
            </div>
            <h1 className="text-6xl font-extrabold leading-tight text-foreground">404</h1>
            <h2 className="mt-2 text-2xl font-semibold text-foreground">Page not found</h2>
            <p className="mt-4 text-sm text-muted-foreground">
              Oops — the page you&apos;re looking for can&apos;t be found.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {user ? (
                <Link
                  to="/dashboard"
                  className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  Go to dashboard
                </Link>
              ) : (
                <Link
                  to="/"
                  className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  Go to sign in
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const gate = (label: string, node: React.ReactNode) => (
  <AuthGate>
    <ErrorBoundary>
      <Suspense fallback={<RouteSuspenseFallback label={label} />}>{node}</Suspense>
    </ErrorBoundary>
  </AuthGate>
);

function AdminOnly({ children }: { children: React.ReactNode }) {
  const { isAdminLike } = useAuth();
  return isAdminLike() ? <>{children}</> : <Navigate to="/dashboard" replace />;
}

const adminGate = (label: string, node: React.ReactNode) =>
  gate(label, <AdminOnly>{node}</AdminOnly>);

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/dashboard" element={gate("Loading dashboard…", <Dashboard />)} />
              <Route path="/my-profile" element={gate("Loading profile…", <MyProfile />)} />
              <Route path="/personnel" element={gate("Loading personnel…", <Personnel />)} />
              <Route path="/office" element={gate("Loading office…", <Office />)} />
              <Route path="/units" element={gate("Loading units…", <Units />)} />
              <Route
                path="/data-reference"
                element={gate("Loading data reference…", <DataReference />)}
              />
              <Route path="/location" element={gate("Loading location…", <Location />)} />
              <Route
                path="/system-access"
                element={gate("Loading system access…", <SystemAccess />)}
              />
              <Route path="/settings" element={adminGate("Loading settings…", <Settings />)} />
              <Route
                path="/available-users"
                element={adminGate("Loading available users…", <AvailableUsers />)}
              />
              <Route
                path="/active-users"
                element={adminGate("Loading active users…", <ActiveUsers />)}
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <Toaster richColors position="top-right" />
            <GlobalLoadingOverlay />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
